/**
 * src/util/ageVerification.js
 *
 * Push 10 — Age verification utilities. Shared by signup validation
 * (src/ducks/auth.duck.js), Stripe Identity webhook handler
 * (server/webhooks/stripe-identity.js), booking-initiation age gate
 * (server-side handler TBD), and waiver Step 1 DOB validation
 * (server/api/sign-link-submit.js).
 *
 * DOB is stored as ISO "YYYY-MM-DD" everywhere on the BoatersNetwork side:
 *   - user.attributes.profile.protectedData.dateOfBirth
 *   - user.attributes.profile.metadata.identityVerifiedDOB
 *   - captures.signerDOB (Step 1 transient state)
 *
 * Native Date math (no third-party date library) — keeps the bundle
 * smaller and the logic explicit. Age math accounts for whether the
 * birthday has passed yet this year (the off-by-one trap).
 *
 * IMPORTANT: every Date math here uses LOCAL timezone constructors.
 * DOB comparison is a per-day decision; sub-day timezone drift is not
 * meaningful for "is this person 18 yet" purposes.
 */

const ISO_DOB_RE = /^(\d{4})-(\d{2})-(\d{2})$/;

/**
 * Parse an ISO "YYYY-MM-DD" string into a Date at local midnight. Returns
 * null on any malformed input (wrong shape, impossible date, etc.) so
 * callers can short-circuit cleanly.
 */
const parseDobISO = dobISO => {
  if (typeof dobISO !== 'string') return null;
  const m = ISO_DOB_RE.exec(dobISO);
  if (!m) return null;
  const year = Number(m[1]);
  const month = Number(m[2]);
  const day = Number(m[3]);
  if (month < 1 || month > 12 || day < 1 || day > 31) return null;
  const d = new Date(year, month - 1, day);
  // Reject e.g. "2025-02-31" — JS rolls the date forward; we want to
  // catch that and treat as invalid.
  if (
    d.getFullYear() !== year ||
    d.getMonth() !== month - 1 ||
    d.getDate() !== day
  ) {
    return null;
  }
  return d;
};

/**
 * Compute integer years between dobISO and asOfDate. Accounts for
 * whether the birthday has occurred yet in asOfDate's year. Returns
 * null on invalid input.
 *
 * Example: dob 2008-06-01, asOf 2026-05-16 → 17 (birthday hasn't passed)
 * Example: dob 2008-06-01, asOf 2026-06-01 → 18 (birthday IS today)
 * Example: dob 2008-06-01, asOf 2026-12-01 → 18
 *
 * @param {string} dobISO ISO YYYY-MM-DD string
 * @param {Date} [asOfDate=new Date()] reference date (defaults to now)
 * @returns {number|null}
 */
const computeAgeYears = (dobISO, asOfDate = new Date()) => {
  const dob = parseDobISO(dobISO);
  if (!dob) return null;
  if (!(asOfDate instanceof Date) || Number.isNaN(asOfDate.getTime())) return null;

  let age = asOfDate.getFullYear() - dob.getFullYear();
  // If the birthday hasn't happened yet this year, subtract 1.
  const monthDelta = asOfDate.getMonth() - dob.getMonth();
  const dayDelta = asOfDate.getDate() - dob.getDate();
  if (monthDelta < 0 || (monthDelta === 0 && dayDelta < 0)) {
    age -= 1;
  }
  return age;
};

/**
 * Convenience: true if dobISO makes the person 18+ as of asOfDate.
 * Returns false on null/invalid (fail-closed — the gate denies access
 * when DOB is missing or malformed, which is the right default).
 */
const isAtLeast18 = (dobISO, asOfDate = new Date()) => {
  const age = computeAgeYears(dobISO, asOfDate);
  return typeof age === 'number' && age >= 18;
};

/**
 * Assemble an ISO "YYYY-MM-DD" string from numeric parts. Zero-pads
 * month/day. Validates by round-tripping through parseDobISO so callers
 * get null for impossible dates (e.g., Feb 31).
 *
 * @param {number} year four-digit year
 * @param {number} month 1-indexed (1 = January)
 * @param {number} day 1-indexed (1-31)
 * @returns {string|null}
 */
const formatDobISO = (year, month, day) => {
  if (
    !Number.isInteger(year) ||
    !Number.isInteger(month) ||
    !Number.isInteger(day)
  ) {
    return null;
  }
  const yyyy = String(year).padStart(4, '0');
  const mm = String(month).padStart(2, '0');
  const dd = String(day).padStart(2, '0');
  const iso = `${yyyy}-${mm}-${dd}`;
  // Round-trip validation rejects e.g. (2025, 2, 31).
  return parseDobISO(iso) ? iso : null;
};

/**
 * Push 10 Phase D.5 — Shared client-side age-gate computation used by
 * every customer-initiated transaction surface (OrderPanel Step 1
 * booking widgets, ProductOrderForm, inquiry / negotiation forms). The
 * gate skips non-customer userTypes (providers, logged-out — handled
 * upstream by their own gates) and the rule is identical across
 * surfaces: customer must have a DOB on profile AND be at least as old
 * as `listing.publicData.minimum_renter_age` (fallback 18).
 *
 * Returns a structured result so callers can choose how to render:
 *   { blocked: false }
 *   { blocked: true, reason: 'dob_missing' }
 *   { blocked: true, reason: 'age_under_min', minimumAge: <number> }
 *
 * Returns `{ blocked: false }` when:
 *   - currentUser is null (logged-out — upstream auth gate handles it)
 *   - userType is not 'customer' (providers / null — not the target)
 *   - DOB is present AND age >= minimum_renter_age
 *
 * Server-side gate at server/api/initiate-privileged.js mirrors this
 * logic as defense-in-depth. The client gate exists to fail-fast UX —
 * surface the message at the booking CTA instead of at checkout submit.
 *
 * @param {Object|null} currentUser the loaded currentUser entity, or null
 * @param {Object} listing the listing entity (must have attributes.publicData)
 * @returns {{ blocked: boolean, reason?: string, minimumAge?: number }}
 */
const computeAgeGateResult = (currentUser, listing) => {
  const userType = currentUser?.attributes?.profile?.publicData?.userType;
  if (userType !== 'customer') {
    return { blocked: false };
  }
  const minimumAge =
    Number(listing?.attributes?.publicData?.minimum_renter_age) || 18;
  const dob = currentUser?.attributes?.profile?.protectedData?.dateOfBirth;
  if (!dob) {
    return { blocked: true, reason: 'dob_missing' };
  }
  const age = computeAgeYears(dob);
  if (age === null || age < minimumAge) {
    return { blocked: true, reason: 'age_under_min', minimumAge };
  }
  return { blocked: false };
};

module.exports = {
  computeAgeYears,
  isAtLeast18,
  formatDobISO,
  parseDobISO,
  computeAgeGateResult,
};
